import {Component, inject, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbRadioModule,
  NbSelectModule, NbToastrService
} from '@nebular/theme';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {combineLatest, Observable} from 'rxjs';
import {ErrorService, QuotePartService, ScopeService, SaveToastService} from '../../../../@core/utils';
import {KPI} from '../../../../@core/api';
import {idMapToArray} from '../../../../@core/utils/scope.service';
import {tap} from "rxjs/operators";
import {QuotePartUpdatePayload} from "../../../../@core/api/models";
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';

@Component({
  selector: 'app-site-quote-part-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    NbCardModule,
    FormsModule,
    ReactiveFormsModule,
    NbOptionModule,
    NbRadioModule,
    NbCheckboxModule,
    NbSelectModule,
    NbInputModule,
    NbButtonModule,
    CommonModule,
    NbIconModule
  ],
  templateUrl: './site-quote-part-dialog.component.html',
  styleUrl: './site-quote-part-dialog.component.scss',
})
export class SiteQuotePartDialogComponent implements OnInit {

  toastrService = inject(NbToastrService);

  translate = inject(TranslateService);

  repartitionForm: FormGroup;
  public investmentSponsors: KPI.Sponsor[];
  submitted = false;

  @Input() siteId: number;
  @Input() id?: number;
  @Input() readOnly?: boolean;

  constructor(private fb: FormBuilder,
              private ref: NbDialogRef<SiteQuotePartDialogComponent>,
              private errorService: ErrorService,
              private quotePartService: QuotePartService,
              private scopeService: ScopeService,
              private nbDialogService: NbDialogService,
              private saveToast: SaveToastService
  ) {
    this.repartitionForm = this.fb.group({
      name: ['', [Validators.required]],
      distribution: this.fb.array([this.createDistributionRow()]),
      refacturable: [null],
      perimeters: this.fb.array([]),
      color_code: [''],
    });
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadQuotePartData();
    } else {
      this.configureRepartitionForm();
    }
  }

  private configureRepartitionForm() {
    combineLatest([
      this.quotePartService.getAssociatedPerimeters(this.siteId),
      this.scopeService.investmentSponsors$.pipe(idMapToArray()),
      this.scopeService.perimeters$.pipe(idMapToArray()),
      this.scopeService.perimeterTypes$.pipe(idMapToArray()),
    ]).subscribe(([associatedPerimeters, investmentSponsors, sites]) => {
      this.investmentSponsors = investmentSponsors;
      const site = sites.find((perimeter) => perimeter.id === this.siteId),
          monoPerimetersLevel1 = site.sub_perimeters
              .filter((perimeter) => !perimeter.level_parent)
      this.repartitionForm.setControl("perimeters", this.fb.array(monoPerimetersLevel1.map(monoPerimeter => {
        const isDisabled = associatedPerimeters.perimeter_ids?.includes(monoPerimeter.id);
        return this.createPerimeterGroup(monoPerimeter, isDisabled, false)
      })))
    });
  }

  private loadQuotePartData() {
    this.quotePartService.getQuotePartDetail(this.id).subscribe(quotePartDetail => {
      combineLatest([
        this.quotePartService.getAssociatedPerimeters(this.siteId),
        this.scopeService.investmentSponsors$.pipe(idMapToArray()),
        this.scopeService.perimeters$.pipe(idMapToArray()),
      ]).subscribe(([associatedPerimeters, investmentSponsors, sites]) => {
        this.investmentSponsors = investmentSponsors;
        //set color
        this.repartitionForm.patchValue({
          color_code: quotePartDetail.color_code,
        });

        const site = sites.find((perimeter) => perimeter.id === this.siteId),
          monoPerimetersLevel1 = site.sub_perimeters
            .filter((perimeter) => !perimeter.level_parent)

        this.repartitionForm.setControl("perimeters", this.fb.array(monoPerimetersLevel1.map(monoPerimeter => {
          const isDisabled = associatedPerimeters.perimeter_ids?.includes(monoPerimeter.id);
          const isChecked = quotePartDetail.perimeters?.includes(monoPerimeter.id);
          return this.createPerimeterGroup(monoPerimeter, isChecked ? false : isDisabled, isChecked)
        })))

        this.repartitionForm.setControl('distribution',
          this.fb.array(
            quotePartDetail.sponsors.map(dist => this.createDistributionRow(dist.id, dist.sponsor_id, dist.percentage, dist.property_code, dist.property_code_label))));

        this.repartitionForm.patchValue({
          name: quotePartDetail.name,
          refacturable: quotePartDetail.refacturable == true ? 'true' : quotePartDetail.refacturable == null ? 'null' : 'false',
        });

        if (this.readOnly) {
          this.repartitionForm.disable();
        }
      });
    });
  }

  get isCreating() {
    return !this.id;
  }

  get perimeterControls(): FormArray {
    return this.repartitionForm.get('perimeters') as FormArray;
  }

  get distributionControls() {
    return this.repartitionForm.get('distribution') as FormArray;
  }

  get isUpdateBtnEnabled() {
    return this.repartitionForm.dirty;
  }

  get isFormValid() {
    const distributionArray = this.distributionControls;
    const allSponsorsValid = distributionArray.controls.every(control => {
      const sponsorId = control.get('sponsor_id')?.value;
      return sponsorId !== null && sponsorId !== undefined && sponsorId !== 0 && sponsorId !== '';
    });
    return allSponsorsValid;
  }

  createPerimeterGroup(perimeter: KPI.Perimeter, isDisabled, isChecked) {
    return this.fb.group({
      name: perimeter.name,
      id: perimeter.id,
      checked: [{value: isChecked, disabled: isDisabled}], // Disable the checkbox
    });
  }

  createDistributionRow(id?:number, sponsor_id?: number, percentage?: number | string, property_code?: string, property_code_label?: string) {
    return this.fb.group({
      id: [id],
      sponsor_id: [{value: sponsor_id, disabled: !this.isCreating}, [Validators.required]],
      percentage: [percentage],
      property_code: [{value: property_code, disabled: !this.isCreating}],
      property_code_label: [{value: property_code_label, disabled: !this.isCreating}],
      quote_part_id: [this.id],
    });
  }

  addRow() {
    if (this.readOnly) {
      return;
    }
    if (parseInt(this.getTotalPercentage()) === 100) {
      return;
    }
    this.distributionControls.push(this.createDistributionRow(0, 0, 0, "", ""));
  }

  removePart(index: number) {
    if (this.readOnly) {
      return;
    }
    this.distributionControls.removeAt(index);
  }

  confirmOK() {
    if (this.readOnly) {
      this.ref.close();
      return;
    }

    this.submitted = true;

    if (!this.isFormValid) {
      this.nbDialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('core.utils.error.validation-title'),
          content: this.translate.instant('quote_part.sponsor_required'),
          canCancel: false
        }
      });
      return;
    }

    const { perimeters, name, refacturable, distribution, color_code } = this.repartitionForm.value;
    //check if name trim is empty
    if (name.trim() === '') {
      this.nbDialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('core.utils.error.validation-title'),
          content: this.translate.instant('quote_part.name_required'),
          canCancel: false
        }
      })
      return;
    }
    const checked_perimeters = perimeters.filter(perimeter => perimeter.checked);

    const total = parseInt(this.getTotalPercentage());
    if (total !== 100) {
      //show error message
      this.nbDialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('core.utils.error.validation-title'),
          content: this.translate.instant('quote_part.percentage_total_error'),
          canCancel: false
        }
      })
      return;
    }
    const payload: QuotePartUpdatePayload = {
      name: name,
      // perimeters: checked_perimeters.map(perimeter => perimeter.id) || [],
      // sponsors: distribution,
      refacturable: refacturable,
      color_code: color_code,
    };
    if (this.repartitionForm.controls['distribution'].dirty) {
      payload['sponsors'] = distribution
    }

    if (this.repartitionForm.controls['perimeters'].dirty) {
      payload['perimeters'] = checked_perimeters.map(perimeter => perimeter.id) || []
    }

    let api: Observable<any>;
    if (this.id) {
      api = this.quotePartService.updateQuotePart(this.id, payload).pipe(
        tap(() => { this.saveToast.showSaved(); }),
      );
    } else {
      const PAYLOAD = {
        site_id: this.siteId,
        ...payload
      }
      api = this.quotePartService.createQuotePart(PAYLOAD).pipe(
        tap(() => { this.saveToast.showSaved(); }),
      );
    }
    api.subscribe(() => {
      this.ref.close({
        reload: true
      });
    }, (err) => this.errorService.handleError(err, true));

  }

  isOptionSelected(id: number): boolean {
    // return this.distributionControls.value.some((dist) => dist.sponsor === id);
    return !id || false
  }

  getTotalPercentage(): string | "-" {
    const value = this.distributionControls.value.reduce((acc, dist) => acc + parseFloat(dist.percentage), 0);
    if (isNaN(value)) {
      return "-";
    }
    return value.toFixed(2);
  }

  close() {
    this.ref.close();
  }

}
